import { IShimmerProps, IStyle, mergeStyleSets, Shimmer, ShimmerElementType, Stack } from "@fluentui/react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { IconButton, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { shimmerColor } from "../../constants/PartnerView.constants";
import { logException } from "../../helper/AppInsightsHelper";
import { IPartnerInfoIdentityDetailsProps } from "../../interfaces/PartnerView.interfaces";
import {
  useGetFavoritePartnersByLoginQuery,
  useInsertOrUpdateFavoritePartnersMutation
} from "../../slices/ExplorePartners.slice";
import { copy } from "../../Utils/js-common";

const partnerInfoIdentityDetailsShimmer = {
  style: {
    marginTop: 10,
    width: 300,
    paddingLeft: 4,
    paddingRight: 3
  },
  shimmerElements: [{ type: ShimmerElementType.line }]
};
const partnerInfoIdentityIdShimmer = {
  style: {
    marginTop: 10,
    width: 200,
    paddingLeft: 4,
    paddingRight: 3
  },
  shimmerElements: [{ type: ShimmerElementType.line, height: 10 }]
};

const commonColor: string = "hsl(206, 100%, 42%)";
const LoadFavoriteIcon = (props: {
  favIcon: boolean;
  updateFavIcon: (status: boolean) => void;
  onFavoriteChange: (isFavorite: boolean) => void;
}) => {
  return (
    <Stack style={{ display: "inline-flex" }}>
      {props.favIcon ? (
        <FavoriteIcon
          titleAccess="Click to unfavorite this partner"
          sx={{
            color: commonColor,
            fontSize: "1.4rem"
          }}
          style={{ cursor: "pointer" }}
          onClick={() => {
            props.updateFavIcon(false);
            props.onFavoriteChange(false);
          }}
        />
      ) : (
        <FavoriteBorderIcon
          titleAccess="Click to favorite this partner"
          sx={{
            color: commonColor,
            fontSize: "1.4rem"
          }}
          style={{ cursor: "pointer" }}
          onClick={() => {
            props.updateFavIcon(true);
            props.onFavoriteChange(true);
          }}
        />
      )}
    </Stack>
  );
};

export const PartnerInfoIdentityDetails = (props: IPartnerInfoIdentityDetailsProps) => {
  const [title, setTitle] = useState("Copy Partner One ID");
  const [isEnabledCopyIcon, setIsEnabledCopyIcon] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const userName = sessionStorage.getItem("loginName")?.toString();
  const userEmail = sessionStorage.getItem("loginUserName")?.toString();
  const [insertOrUpdateFavoritePartners] = useInsertOrUpdateFavoritePartnersMutation();
  const { data: favoritePartnersData, error: favoritePartnersError, refetch } = useGetFavoritePartnersByLoginQuery();

  useEffect(() => {
    if (favoritePartnersData && Array.isArray(favoritePartnersData)) {
      const partnerData = favoritePartnersData.find((partner) => partner.partnerOneId === props.partnerOneID);
      if (partnerData) {
        setIsFavorite(partnerData.isFavorite);
      }
    } else if (favoritePartnersError) {
      const customProps = {
        PartnerId: props.partnerOneID,
        mpnId: props.mpnId
      };
      logException(
        "Partner Page",
        "PartnerInfoIdentityDetails",
        "getFavoritePartners",
        favoritePartnersError,
        customProps
      );
    }
  }, [favoritePartnersData, favoritePartnersError, props.partnerOneID]);

  const styles = mergeStyleSets({
    partnerInfoIdentityDetailsStack: {
      display: "initial",
      paddingLeft: 10
    } as IStyle,
    partnerInfoIdentityDetailsStackLabel: {
      fontSize: 22,
      fontWeight: "400",
      fontSizeAdjust: "initial"
    } as IStyle,
    partnerInfoTenantIdFlexContainer: {
      display: "inline-flex",
      alignItems: "center",
      border: isEnabledCopyIcon ? "1px solid" : "1px solid transparent",
      padding: "2px 2px 2px 0px"
    } as IStyle,
    FontSize: {
      fontSize: 12
    },
    partnerInfoTenantIdTooltipContainer: {
      visibility: isEnabledCopyIcon ? "visible" : "hidden",
      position: "sticky"
    },
    partnerInfoTenantIdTooltipContainerBtn: { color: "#000 !important" }
  });
  const onMouseEnterTenantId = () => {
    setIsEnabledCopyIcon(true);
    setTitle("Copy Partner One ID");
  };
  const onMouseLeaveTenantId = () => setIsEnabledCopyIcon(false);
  const copyValue = async () => {
    await copy(props.partnerOneID as string);
    setTitle("Copied");
  };

  const handleFavoriteChange = (isFavorite: boolean) => {
    insertOrUpdateFavoritePartners({
      partnerOneId: props.partnerOneID ?? "",
      partnerOneName: props.name ?? "",
      loginName: userName ?? "",
      loginEmail: userEmail ?? "",
      isFavorite: isFavorite
    }).then(() => {
      refetch();
    });
  };

  return (
    <Stack className={styles.partnerInfoIdentityDetailsStack}>
      <LoadShimmer shimmerProps={partnerInfoIdentityDetailsShimmer} loading={props.loading} />
      {!props.loading && (
        <>
          <label className={styles.partnerInfoIdentityDetailsStackLabel}>{props.name}&nbsp;</label>
          <LoadFavoriteIcon
            favIcon={isFavorite}
            updateFavIcon={setIsFavorite}
            onFavoriteChange={handleFavoriteChange}
          />
        </>
      )}

      <LoadShimmer shimmerProps={partnerInfoIdentityIdShimmer} loading={props.loading} />
      {!props.loading && (
        <>
          <br />
          <div
            className={styles.partnerInfoTenantIdFlexContainer}
            onMouseEnter={onMouseEnterTenantId}
            onMouseLeave={onMouseLeaveTenantId}
          >
            <label className={styles.FontSize}>PartnerOneID : {props.partnerOneID}</label>
            <div className={styles.partnerInfoTenantIdTooltipContainer}>
              <Tooltip title={title} leaveDelay={500} arrow>
                <IconButton
                  size="small"
                  onClick={copyValue}
                  onMouseEnter={onMouseEnterTenantId}
                  className={styles.partnerInfoTenantIdTooltipContainerBtn}
                >
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </>
      )}

      <LoadShimmer shimmerProps={partnerInfoIdentityIdShimmer} loading={props.loading} />
      {!props.loading && (
        <>
          <br />
          <label style={{ fontSize: 12 }}>
            PartnerID : {props.mpnId} | PartnerName : {props.mpnName}
          </label>
        </>
      )}
    </Stack>
  );
};

const LoadShimmer = (props: { shimmerProps: IShimmerProps; loading: boolean }) => {
  return props.loading ? <Shimmer {...props.shimmerProps} shimmerColors={{ shimmer: shimmerColor }}></Shimmer> : null;
};
